import { useState, useEffect, useCallback } from 'react';
import { API_TIMEOUT, buildApiUrl } from '../config';

const apiRequest = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  
  const config = {
    headers: { ...defaultHeaders, ...options.headers },
    ...options
  };
  
  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.mensaje || `HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      data,
      error: data.error,
      mensaje: data.mensaje
    };
    
  } catch (error) {
    throw error;
  }
};

export const useEventos = (options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const {
    immediate = false
  } = options;

  const fetchEventos = useCallback(async () => {
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(buildApiUrl('eventos'), {
        signal: controller.signal,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const validatedData = result.data || result;
      setData(validatedData);
      return validatedData;
    } catch (err) {
      const errorMessage = err.name === 'AbortError' 
        ? 'Tiempo de espera agotado' 
        : 'Error de conexión con el servidor';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (immediate) {
      fetchEventos();
    }
  }, [immediate, fetchEventos]);

  return {
    data,
    loading,
    error,
    refetch: fetchEventos,
  };
};

export const getEventoById = async (id) => {
  return apiRequest(`evento/${id}`, { method: 'GET' });
};

export const createEvento = async (eventoData) => {
  const dataToSend = {
    titulo: eventoData.Titulo || eventoData.titulo,
    fechaCreacion: eventoData.Fecha_creacion || eventoData.fechaCreacion,
    fechaFin: eventoData.Fecha_fin || eventoData.fechaFin,
    mes: eventoData.Mes || eventoData.mes,
    ubicacion: eventoData.Ubicacion || eventoData.ubicacion,
    url_File: eventoData.Url_File || eventoData.url_File || eventoData.url_File,
    fileName: eventoData.FileName || eventoData.fileName
  };
  return apiRequest('evento', {
    method: 'POST',
    body: JSON.stringify(dataToSend)
  });
};

export const updateEvento = async (id, eventoData) => {
  const dataToSend = {
    titulo: eventoData.Titulo || eventoData.titulo,
    fechaCreacion: eventoData.Fecha_creacion || eventoData.fechaCreacion,
    fechaFin: eventoData.Fecha_fin || eventoData.fechaFin,
    mes: eventoData.Mes || eventoData.mes,
    ubicacion: eventoData.Ubicacion || eventoData.ubicacion,
    url_File: eventoData.Url_File || eventoData.url_File,
    fileName: eventoData.FileName || eventoData.fileName
  };
  return apiRequest(`evento/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dataToSend)
  });
};

export const deleteEvento = async (id) => {
  return apiRequest('eliminarEvento', {
    method: 'POST',
    body: JSON.stringify({ pIdEvento: id })
  });
};

export default {
  useEventos,
  getEventoById,
  createEvento,
  updateEvento,
  deleteEvento
};
